import React, {FC, memo, ReactNode, useContext, useReducer, useState} from 'react';
import cx from 'classnames';
import {HTMLDialog, TreeNodeProps} from '@priority-software/priority-style-react';
import dialogCSS from './common-dialog-styles.module.scss';
import {emailDialogID} from './testIDs';
import {emailReducer} from './reducer';
import {useEmailContextAPI} from './hooks/useEmailContextAPI';
import {DialogToolbarContent} from '../../components/Dialog/DialogToolbar/DialogToolbar';
import {EmailContext} from './context';
import {BodyEmailEditor} from './components/BodyEmailEditor';
import {FooterEmailEditor} from './components/FooterEmailEditor';
import {TVoidFunction} from './types';
import {TEmailContextType, TEmailReducerType} from './reducer/types';
import {renderDefault} from './utils/common';
import {priority} from '@priority-software/priority-proto-api/lib';
import {useHandlePressKey} from './components/RichText/parts/hooks/useHandleKeyPress';
import {createDataTestId} from '../../utils/dataTestId';
import {texts} from './texts';
import {texts as textsAutomation} from '../Automations/texts';
import {AutomationContext} from '../Automations/context';
interface IEmailDialogProps {
    isOpen: boolean;
    toolbarContent?: ReactNode;
    onClose?: TVoidFunction;
    messagesValues?: IMessagesValues;
    treeValues: TreeNodeProps;
}

export type IMessagesValues = {
    Messages: {
        [key: number]: priority.netitems.form.IAlertMessage;
    };
    activeMessageLang: string;
    initActiveBySubject: priority.netitems.form.IAlertMessage;
};

export const EmailDialog: FC<IEmailDialogProps> = memo(
    ({isOpen, onClose, messagesValues, treeValues}) => {
        const [contextValue, dispatch] = useReducer<TEmailReducerType, TEmailContextType>(
            (state, action) => emailReducer(state, action, messagesValues),
            renderDefault(messagesValues, treeValues),
            _ => renderDefault(messagesValues, treeValues)
        );

        const contextHandlers = useEmailContextAPI({
            dispatch,
            contextValue
        });

        const {openRule} = useContext(AutomationContext);

        const [focusSubject, setFocusSubject] = useState(false);

        useHandlePressKey({
            keyTyped: 'keydown',
            keyPressed: 'Escape',
            cb: () => contextHandlers.onBack(onClose)
        });

        if (!isOpen) {
            return null;
        }

        return (
            <EmailContext.Provider
                value={{
                    states: {...contextValue.states, focusSubject},
                    handlers: {...contextHandlers, setFocusSubject}
                }}
            >
                <HTMLDialog
                    {...createDataTestId('email-dialog', true)}
                    id={emailDialogID}
                    escClosable
                    isOpen={isOpen}
                    onClose={() => contextHandlers.onBack(onClose)}
                    componentClasses={cx(
                        dialogCSS.component,
                        dialogCSS.paddingBottom0,
                        'reactComp'
                    )}
                    toolbarContent={
                        <DialogToolbarContent
                            mainTitle={texts.email_editor}
                            subTitle={openRule.AlertNumber?.toString() ?? textsAutomation.new_label}
                            description={openRule.Description}
                            helpText={'help text dialog'}
                            tooltipPortalID={emailDialogID}
                        />
                    }
                    content={<BodyEmailEditor />}
                    footer={<FooterEmailEditor {...{onClose, treeValues}} />}
                    size="large"
                />
            </EmailContext.Provider>
        );
    }
);
